import { Loader2 } from 'lucide-react';
import React, { useState } from 'react';

import { supabase } from '../../../lib/supabase';
import type { Database } from '../../../types/supabase';

interface Subscription {
  id: string;
  name: string;
  price: number;
  subscription_type: string;
  access_type: string;
  access_value: number;
  contract_term: string;
  billing_cycle: string;
  prepayment_required: boolean;
  prepayment_months: number;
  is_free_trial: boolean;
  requires_approval: boolean;
  allows_cash_payment: boolean;
  trial_duration_days: number | null;
  trial_duration_classes: number | null;
  trial_duration_period: string | null;
  created_at: string;
  sports_included: string[];
  amenities_included: string[];
}

interface SubscriptionFormProps {
  companyId: string;
  subscription: Subscription | null;
  onSave: (subscription: Subscription) => void;
  onCancel: () => void;
}

export function SubscriptionForm({ companyId, subscription, onSave, onCancel }: SubscriptionFormProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: subscription?.name || '',
    price: subscription?.price?.toString() || '',
    subscriptionType: subscription?.subscription_type || 'recurring',
    accessType: subscription?.access_type || 'unlimited',
    accessValue: subscription?.access_value?.toString() || '',
    contractTerm: subscription?.contract_term || 'monthly',
    billingCycle: subscription?.billing_cycle || 'monthly',
    prepaymentRequired: subscription?.prepayment_required || false,
    prepaymentMonths: subscription?.prepayment_months?.toString() || '',
    isFreeTrial: subscription?.is_free_trial || false,
    requiresApproval: subscription?.requires_approval ?? true,
    allowsCashPayment: subscription?.allows_cash_payment || false,
    // Trial duration fields
    trialDurationType: subscription?.trial_duration_days ? 'days' : 
                       subscription?.trial_duration_classes ? 'classes' : 
                       subscription?.trial_duration_period ? 'period' : 'days',
    trialDurationDays: subscription?.trial_duration_days?.toString() || '',
    trialDurationClasses: subscription?.trial_duration_classes?.toString() || '',
    trialDurationPeriod: subscription?.trial_duration_period || '30 days',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Subscription name is required');
      return;
    }
    if (!form.price && !form.isFreeTrial) {
      setError('Price is required');
      return;
    }
    if (form.prepaymentRequired && (!form.prepaymentMonths || parseInt(form.prepaymentMonths) <= 0)) {
      setError('Pre-payment months must be greater than 0 when pre-payment is required');
      return;
    }
    // Validate trial duration
    if (form.subscriptionType === 'trial') {
      const hasDays = form.trialDurationType === 'days' && form.trialDurationDays && parseInt(form.trialDurationDays) > 0;
      const hasClasses = form.trialDurationType === 'classes' && form.trialDurationClasses && parseInt(form.trialDurationClasses) > 0;
      const hasPeriod = form.trialDurationType === 'period' && form.trialDurationPeriod;
      if (!hasDays && !hasClasses && !hasPeriod) {
        setError('Please specify trial duration (days, classes, or period)');
        return;
      }
    }

    setSaving(true);
    setError(null);

    const data: Partial<Database['public']['Tables']['company_subscriptions']['Insert']> = {
      company_id: companyId,
      name: form.name.trim(),
      price: form.isFreeTrial ? 0 : parseFloat(form.price) || 0,
      subscription_type: form.subscriptionType,
      access_type: form.accessType,
      access_value: form.accessType === 'unlimited' ? 0 : parseInt(form.accessValue) || 0,
      contract_term: form.contractTerm,
      billing_cycle: form.billingCycle,
      prepayment_required: form.prepaymentRequired,
      prepayment_months: form.prepaymentRequired ? parseInt(form.prepaymentMonths) || 0 : 0,
      is_free_trial: form.isFreeTrial,
      requires_approval: form.requiresApproval,
      allows_cash_payment: form.allowsCashPayment,
      sports_included: [],
      amenities_included: [],
    };

    // Set trial duration based on type
    if (form.subscriptionType === 'trial') {
      if (form.trialDurationType === 'days') {
        data.trial_duration_days = parseInt(form.trialDurationDays) || null;
        data.trial_duration_classes = null;
        data.trial_duration_period = null;
      } else if (form.trialDurationType === 'classes') {
        data.trial_duration_classes = parseInt(form.trialDurationClasses) || null;
        data.trial_duration_days = null;
        data.trial_duration_period = null;
      } else if (form.trialDurationType === 'period') {
        data.trial_duration_period = form.trialDurationPeriod || null;
        data.trial_duration_days = null;
        data.trial_duration_classes = null;
      }
    } else {
      // Clear trial duration fields for non-trial subscriptions
      data.trial_duration_days = null;
      data.trial_duration_classes = null;
      data.trial_duration_period = null;
    }

    try {
      if (subscription) {
        // Update
        const { data: updated, error: updateError } = await supabase
          .from('company_subscriptions')
          .update(data)
          .eq('id', subscription.id)
          .select()
          .single();

        if (updateError) throw updateError;
        onSave(updated);
      } else {
        // Create
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: created, error: createError } = await (supabase.from as any)('company_subscriptions')
          .insert(data)
          .select()
          .single();

        if (createError) throw createError;
        onSave(created);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save subscription');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-[#EBFFEE]">
          {subscription ? 'Edit Subscription' : 'Create Subscription'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 max-h-[60vh] overflow-y-auto">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Subscription Name <span className="text-rollops-accent">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., Basic Monthly Plan"
            className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
            required
            maxLength={100}
          />
        </div>

        {/* Subscription Type */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Subscription Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {(['recurring', 'punch_card', 'trial', 'drop_in'] as const).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setForm({ ...form, subscriptionType: type })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.subscriptionType === type
                    ? 'bg-[#9B1421] text-white'
                    : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                }`}
              >
                {type === 'recurring' ? 'Recurring' :
                 type === 'punch_card' ? 'Punch Card' :
                 type === 'trial' ? 'Trial' : 'Drop-In'}
              </button>
            ))}
          </div>
        </div>

        {/* Price */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Price (USD)
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A9A3A3]">$</span>
            <input
              type="number"
              step="0.01"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              placeholder="99.00"
              disabled={form.isFreeTrial}
              maxLength={10}
              className="w-full pl-8 pr-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 disabled:opacity-50"
            />
          </div>
          {form.subscriptionType === 'trial' && (
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isFreeTrial}
                onChange={(e) => setForm({ ...form, isFreeTrial: e.target.checked })}
                className="w-4 h-4 rounded border-[#504A4A] bg-[#504A4A] text-rollops-accent focus:ring-[#9B1421]"
              />
              <span className="text-sm text-[#A9A3A3]">Free trial</span>
            </label>
          )}
        </div>

        {/* Access Level */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Access Level
          </label>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, accessType: 'days' })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                form.accessType === 'days'
                  ? 'bg-[#9B1421] text-white'
                  : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
              }`}
            >
              Days
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, accessType: 'classes' })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                form.accessType === 'classes'
                  ? 'bg-[#9B1421] text-white'
                  : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
              }`}
            >
              Classes
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, accessType: 'unlimited', accessValue: '' })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                form.accessType === 'unlimited'
                  ? 'bg-[#9B1421] text-white'
                  : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
              }`}
            >
              Unlimited
            </button>
          </div>
          {form.accessType !== 'unlimited' && (
            <input
              type="number"
              value={form.accessValue}
              onChange={(e) => setForm({ ...form, accessValue: e.target.value })}
              placeholder={`Number of ${form.accessType}`}
              maxLength={6}
              className="w-full mt-2 px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
            />
          )}
        </div>

        {/* Trial Duration (Trial subscriptions only) */}
        {form.subscriptionType === 'trial' && (
          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
              Trial Duration Type
            </label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <button
                type="button"
                onClick={() => setForm({ ...form, trialDurationType: 'days' })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.trialDurationType === 'days'
                    ? 'bg-[#9B1421] text-white'
                    : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                }`}
              >
                Days
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, trialDurationType: 'classes' })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.trialDurationType === 'classes'
                    ? 'bg-[#9B1421] text-white'
                    : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                }`}
              >
                Classes
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, trialDurationType: 'period' })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.trialDurationType === 'period'
                    ? 'bg-[#9B1421] text-white'
                    : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                }`}
              >
                Period
              </button>
            </div>

            {form.trialDurationType === 'days' && (
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                  Number of Days
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.trialDurationDays}
                  onChange={(e) => setForm({ ...form, trialDurationDays: e.target.value })}
                  placeholder="e.g., 30"
                  maxLength={3}
                  className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                />
              </div>
            )}

            {form.trialDurationType === 'classes' && (
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                  Number of Classes
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.trialDurationClasses}
                  onChange={(e) => setForm({ ...form, trialDurationClasses: e.target.value })}
                  placeholder="e.g., 5"
                  maxLength={3}
                  className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                />
              </div>
            )}

            {form.trialDurationType === 'period' && (
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                  Trial Period
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['7 days', '14 days', '30 days', '60 days', '90 days'].map((period) => (
                    <button
                      key={period}
                      type="button"
                      onClick={() => setForm({ ...form, trialDurationPeriod: period })}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        form.trialDurationPeriod === period
                          ? 'bg-[#9B1421] text-white'
                          : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                      }`}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Contract Term (Recurring only) */}
        {form.subscriptionType === 'recurring' && (
          <>
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                Contract Term
              </label>
              <select
                value={form.contractTerm}
                onChange={(e) => setForm({ ...form, contractTerm: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="semi_annual">Semi-Annual</option>
                <option value="annual">Annual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                Billing Cycle
              </label>
              <select
                value={form.billingCycle}
                onChange={(e) => setForm({ ...form, billingCycle: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
              </select>
            </div>
          </>
        )}

        {/* Prepayment (Recurring only) */}
        {form.subscriptionType === 'recurring' && (
          <div>
            <label className="flex items-center gap-3 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={form.prepaymentRequired}
                onChange={(e) => setForm({ ...form, prepaymentRequired: e.target.checked, prepaymentMonths: e.target.checked ? form.prepaymentMonths : '' })}
                className="w-5 h-5 rounded border-[#504A4A] bg-[#504A4A] text-rollops-accent focus:ring-[#9B1421]"
              />
              <span className="text-sm text-[#EBFFEE]">Require Pre-payment</span>
            </label>
            {form.prepaymentRequired && (
              <div className="ml-8">
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                  Pre-payment Months (0-24)
                </label>
                <input
                  type="number"
                  min="0"
                  max="24"
                  value={form.prepaymentMonths}
                  onChange={(e) => {
                    const value = e.target.value;
                    const num = parseInt(value, 10);
                    if (value === '' || (!isNaN(num) && num >= 0 && num <= 24)) {
                      setForm({ ...form, prepaymentMonths: value });
                    }
                  }}
                  placeholder="e.g., 3"
                  maxLength={3}
                  className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                />
              </div>
            )}
          </div>
        )}

        {/* Options */}
        <div className="space-y-3">
          {form.subscriptionType === 'trial' && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.requiresApproval}
                onChange={(e) => setForm({ ...form, requiresApproval: e.target.checked })}
                className="w-5 h-5 rounded border-[#504A4A] bg-[#504A4A] text-rollops-accent focus:ring-[#9B1421]"
              />
              <span className="text-sm text-[#EBFFEE]">Requires admin approval</span>
            </label>
          )}

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.allowsCashPayment}
              onChange={(e) => setForm({ ...form, allowsCashPayment: e.target.checked })}
              className="w-5 h-5 rounded border-[#504A4A] bg-[#504A4A] text-rollops-accent focus:ring-[#9B1421]"
            />
            <span className="text-sm text-[#EBFFEE]">Allow cash payment</span>
          </label>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Saving...
              </>
            ) : (
              subscription ? 'Update' : 'Create'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
